import { classes } from 'html-classes';
import type { PropsWithChildren } from 'react';

import { company } from '~/company/Company';
import { CompanyName } from '~/company/interface';

/**
 * Props for the HeroBannerFrame component
 */
interface HeroBannerFrameProps {
  /** Optional CSS class name applied to the outer container */
  className?: string;
}

/** Base geometry shared by every company (full width + background). */
const CONTAINER_BASE = 'relative w-full bg-[var(--navigation-bg)]';

/**
 * Aspect-ratio classes per company. Numbers mirror getHeroBannerSizes() / the CMS
 * BANNER_LINK config; Tailwind needs literal class strings, so they are spelled
 * out here rather than interpolated.
 */
const ASPECT_DEFAULT =
  'min-[1025px]:aspect-[1920/888] max-[1024px]:aspect-[360/487]';
const ASPECT_MCOSMETIC =
  'min-[1025px]:aspect-[1920/848] max-[1024px]:aspect-[390/492]';

/** Default V3 shape: rounded top corners. */
const DEFAULT_SHAPE = `overflow-hidden rounded-t-[var(--border-radius--70)] max-[1024px]:rounded-t-[var(--border-radius--50)] ${ASPECT_DEFAULT}`;
/** "saas" company set: all corners squared. */
const SAAS_SHAPE = `overflow-hidden rounded-none ${ASPECT_DEFAULT}`;
/** MCosmetic: visible overflow, rounded bottom corners, taller image ratio. */
const MCOSMETIC_SHAPE = `overflow-visible rounded-t-none rounded-b-[var(--border-radius--70)] ${ASPECT_MCOSMETIC}`;

/**
 * Shared geometric frame for the hero banner and its loading skeleton.
 *
 * Owns the container layout, aspect-ratio, background and rounded corners
 * (including company-specific overrides via `company.match`) so that the
 * banner and the skeleton occupy the exact same area. This guarantees there is
 * no layout shift or visual jump when the skeleton is replaced by the real
 * banner.
 */
const HeroBannerFrame = ({
  className,
  children,
}: PropsWithChildren<HeroBannerFrameProps>) => {
  const containerShape = company.match({
    [CompanyName.MCosmetic]: MCOSMETIC_SHAPE,
    [CompanyName.Heimkaup]: SAAS_SHAPE,
    [CompanyName.Localee]: SAAS_SHAPE,
    [CompanyName.Cerise]: SAAS_SHAPE,
    [CompanyName.CircleK]: SAAS_SHAPE,
    [CompanyName.Electronium]: SAAS_SHAPE,
    [CompanyName.DubaiExpress]: SAAS_SHAPE,
    [CompanyName.DubaiExpressCom]: SAAS_SHAPE,
    default: DEFAULT_SHAPE,
  });

  return (
    <section className={classes([CONTAINER_BASE, containerShape, className])}>
      {/* I DONT KNOW WHY, BUT IT EMPTY DIV FIXED HEADER CORNERS FOR WINDOW CHROME */}
      <div className="fixed top-[var(--header-height)] h-0.5 w-0.5 opacity-0" />

      <div className="relative mx-auto h-full w-full">{children}</div>
    </section>
  );
};

export default HeroBannerFrame;
